<template>
  <div v-loading="loading" class="nx-page nx-report-page kanban-power-room">
    <nx-report-header title="配电室环境监测">
      <div class="pr-actions">
        <el-select
          v-model="queryRoomCode"
          clearable
          filterable
          size="small"
          placeholder="全部配电室"
          class="pr-room-select"
          @change="loadData"
        >
          <el-option
            v-for="room in roomOptions"
            :key="room.roomCode"
            :label="room.roomName"
            :value="room.roomCode"
          />
        </el-select>
        <el-button type="primary" size="small" icon="el-icon-refresh" @click="loadData">刷新</el-button>
      </div>
    </nx-report-header>

    <el-empty v-if="!rooms.length && !loading" description="暂无配电室接入数据" />

    <template v-else>
      <div class="pr-room-tabs">
        <button
          v-for="room in rooms"
          :key="room.roomCode"
          type="button"
          :class="['pr-room-tab', { active: activeRoomCode === room.roomCode, alarm: roomAlarmCount(room) > 0 }]"
          @click="activeRoomCode = room.roomCode"
        >
          <span>{{ room.roomName }}</span>
          <em>{{ communicationText(room.communicationNormal) }}</em>
        </button>
      </div>

      <div class="pr-kpi-row">
        <div class="pr-kpi communication">
          <div class="label">通信状态</div>
          <div :class="['value', communicationClass(currentRoom.communicationNormal)]">{{ communicationText(currentRoom.communicationNormal) }}</div>
          <div class="sub">{{ currentRoom.protocolType || 'IEC104' }}</div>
        </div>
        <div class="pr-kpi points">
          <div class="label">接入点位</div>
          <div class="value">{{ envSummary.totalPointCount || 0 }}<span>个</span></div>
          <div class="sub">环境遥信点位</div>
        </div>
        <div class="pr-kpi alarms">
          <div class="label">当前告警</div>
          <div :class="['value', (envSummary.activeAlarmCount || 0) > 0 ? 'danger' : 'ok']">{{ envSummary.activeAlarmCount || 0 }}<span>个</span></div>
          <div class="sub">实时点位状态</div>
        </div>
        <div class="pr-kpi today">
          <div class="label">今日告警</div>
          <div class="value warn">{{ envSummary.todayAlarmCount || 0 }}<span>次</span></div>
          <div class="sub">按采集历史统计</div>
        </div>
      </div>

      <div class="pr-main-grid">
        <div class="pr-card area-card">
          <div class="pr-card-head">
            <strong>重点区域状态</strong>
            <small>正常与异常区域全部展示</small>
          </div>
          <div class="pr-area-grid">
            <div
              v-for="area in currentRoom.areaRows || []"
              :key="area.areaName"
              :class="['pr-area-item', area.normal ? 'normal' : 'alarm']"
            >
              <div class="area-title">
                <span>{{ area.areaName }}</span>
                <el-tag size="mini" :type="area.normal ? 'success' : 'danger'">{{ area.normal ? '正常' : '告警' }}</el-tag>
              </div>
              <div class="area-desc">{{ area.statusText || '--' }}</div>
              <div class="area-count">告警 {{ area.alarmCount || 0 }} 项</div>
            </div>
          </div>
        </div>

        <div class="pr-side">
          <div class="pr-card">
            <div class="pr-card-head">
              <strong>告警类型统计</strong>
            </div>
            <div class="pr-type-list">
              <div v-for="item in currentRoom.typeStats || []" :key="item.signalType" class="pr-type-item">
                <div>
                  <span>{{ item.signalType }}</span>
                  <small>{{ item.totalCount || 0 }} 个点位</small>
                </div>
                <strong :class="(item.alarmCount || 0) > 0 ? 'danger' : 'ok'">{{ item.alarmCount || 0 }}</strong>
              </div>
            </div>
          </div>

          <div class="pr-card">
            <div class="pr-card-head">
              <strong>电量接入预留</strong>
            </div>
            <div class="pr-electric">
              <div>
                <label>接入状态</label>
                <span :class="electricSummary.enabled ? 'ok' : 'muted'">{{ electricSummary.enabled ? '已接入' : '未接入' }}</span>
              </div>
              <div>
                <label>累计电量</label>
                <span>{{ valueText(electricSummary.todayPower) }} {{ electricSummary.unit || 'kWh' }}</span>
              </div>
              <div>
                <label>当前负荷</label>
                <span>{{ valueText(electricSummary.currentLoad) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="pr-card">
        <div class="pr-card-head">
          <strong>点位明细</strong>
          <small>{{ currentRoom.roomName || '--' }}</small>
        </div>
        <el-table :data="currentRoom.points || []" size="small" border stripe empty-text="暂无点位数据">
          <el-table-column prop="areaName" label="区域" min-width="110" />
          <el-table-column prop="pointName" label="点位名称" min-width="210" show-overflow-tooltip />
          <el-table-column prop="signalType" label="类型" width="90" />
          <el-table-column prop="signalKind" label="信号" width="80" />
          <el-table-column prop="ioa" label="遥信码/IOA" width="105" align="center" />
          <el-table-column label="当前值" width="110" align="center">
            <template slot-scope="{ row }">{{ pointValueText(row) }}</template>
          </el-table-column>
          <el-table-column label="状态" width="90" align="center">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="row.alarmStatus === 1 ? 'danger' : 'success'">{{ row.statusText || statusText(row.alarmStatus) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="质量" width="110" align="center">
            <template slot-scope="{ row }">{{ row.quality || '--' }}</template>
          </el-table-column>
          <el-table-column label="最后采集" width="170">
            <template slot-scope="{ row }">{{ formatTime(row.lastCollectTime) }}</template>
          </el-table-column>
        </el-table>
      </div>
    </template>
  </div>
</template>

<script>
import NxReportHeader from '@/components/nx/report/NxReportHeader.vue'
import { fetchPowerRoomMonitor } from './api'

function emptyRoom() {
  return {
    roomCode: '',
    roomName: '',
    protocolType: 'IEC104',
    communicationNormal: null,
    lastCollectTime: null,
    envSummary: {},
    electricSummary: {},
    typeStats: [],
    areaRows: [],
    points: []
  }
}

export default {
  name: 'KanbanPowerRoomMonitoring',
  components: { NxReportHeader },
  data() {
    return {
      loading: false,
      queryRoomCode: '',
      activeRoomCode: '',
      dashboard: {
        generatedTime: null,
        rooms: []
      }
    }
  },
  computed: {
    rooms() {
      return this.dashboard.rooms || []
    },
    roomOptions() {
      return this.rooms
    },
    currentRoom() {
      if (!this.rooms.length) return emptyRoom()
      return this.rooms.find(item => item.roomCode === this.activeRoomCode) || this.rooms[0]
    },
    envSummary() {
      return this.currentRoom.envSummary || {}
    },
    electricSummary() {
      return this.currentRoom.electricSummary || {}
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const params = { includeNormalArea: true }
        if (this.queryRoomCode) params.roomCode = this.queryRoomCode
        const raw = await fetchPowerRoomMonitor(params)
        this.dashboard = {
          generatedTime: raw && raw.generatedTime,
          rooms: raw && raw.rooms ? raw.rooms : []
        }
        if (!this.rooms.some(item => item.roomCode === this.activeRoomCode)) {
          this.activeRoomCode = this.rooms.length ? this.rooms[0].roomCode : ''
        }
      } catch (e) {
        this.$message.error((e && e.message) || '加载失败')
      } finally {
        this.loading = false
      }
    },
    roomAlarmCount(room) {
      return room && room.envSummary ? room.envSummary.activeAlarmCount || 0 : 0
    },
    communicationText(value) {
      if (value === true) return '正常'
      if (value === false) return '中断'
      return '未知'
    },
    communicationClass(value) {
      if (value === true) return 'ok'
      if (value === false) return 'danger'
      return 'muted'
    },
    statusText(value) {
      return value === 1 ? '告警' : '正常'
    },
    valueText(value) {
      return value === null || value === undefined || value === '' ? '--' : value
    },
    pointValueText(row) {
      if (!row) return '--'
      if (row.numericValue !== null && row.numericValue !== undefined) {
        return `${row.numericValue}${row.unit || ''}`
      }
      if (row.textValue) return row.textValue
      return this.valueText(row.stateValue)
    },
    formatTime(value) {
      if (!value) return '--'
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return '--'
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    }
  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';
</style>
